import CheckCircle from "@assets/images/check-circle.svg";
import InfoIcon from "@assets/images/info.png";
import XCircle from "@assets/images/x-circle.svg";
import { Box, Typography } from "@material-ui/core";
import Container from "@saleor/components/Container";
import MyTypography from "@saleor/components/MyTypography";
import PageHeader from "@saleor/components/PageHeader";
import { defaultListSettings } from "@saleor/config";
import DeliveryReportFilterSectionCustomer from "@saleor/deliveryReport/components/DeliveryReportFilterSection";
import DeliveryReportTableCustomer from "@saleor/deliveryReport/components/DeliveryReportTableCustomer";
import {
  DeliveryReportSortField,
  deliveryReportUrl,
  DeliveryReportUrlQueryParams
} from "@saleor/deliveryReport/urls";
import {
  LmsReportCsCustomerInput,
  useGetLmsReportCsCustomerLazyQuery
} from "@saleor/graphql";
import useAppState from "@saleor/hooks/useAppState";
import useListSettings from "@saleor/hooks/useListSettings";
import { useLocalPaginationState } from "@saleor/hooks/useLocalPaginator";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginationLocal from "@saleor/hooks/usePaginationLocal";
import { makeStyles } from "@saleor/macaw-ui";
import { ListViews } from "@saleor/types";
import { getSortParams, getSortUrlVariables } from "@saleor/utils/sort";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { DataItemCommon, sortingItemList } from "../utils/sort";

export interface OrdersViewProps {
  params: DeliveryReportUrlQueryParams;
}

interface StyleProps {
  fullScreen?: boolean;
}

const useStyles = makeStyles<StyleProps>(
  () => ({
    container: {
      marginBottom: "7rem"
    },
    pageHeader: {
      marginBottom: "1.2rem",
      "& > :first-child": {
        visibility: props => (props.fullScreen ? "hidden" : "visible"),
        margin: 0,
        marginBottom: props => (props.fullScreen ? "0.4rem" : "1.2rem")
      },
      "& > :nth-child(2)": {
        alignSelf: "flex-end"
      }
    },
    searchBar: {
      display: props => (props.fullScreen ? "none" : "block")
    },
    flex: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center"
    }
  }),
  { name: "DeliveryReportCustomer" }
);

function SectionMessage(props) {
  const classes = useStyles({});
  return (
    <Box className={classes.flex}>
      <img src={props.icon} alt="info" />
      <Box ml={1}>
        <MyTypography
          variant="body3"
          style={{
            color: props.color
          }}
          color={props.color}
        >
          {props.message}
        </MyTypography>
      </Box>
    </Box>
  );
}

const DeliveryReportCustomer: React.FC<OrdersViewProps> = ({ params }) => {
  const intl = useIntl();
  const [{ fullScreen }, dispatchAppState] = useAppState();
  const navigate = useNavigator();

  const classes = useStyles({ fullScreen });
  const [filter, setFilter] = useState({});
  const [firstRender, setFirstRender] = useState<boolean>(true);

  const [fetchData, { loading, data }] = useGetLmsReportCsCustomerLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    },
    onError: () => {
      dispatchAppState({
        payload: {
          value: false
        },
        type: "displayLoader"
      });
    }
  });

  const [baseListDataItem, setBaseListDataItem] = useState<DataItemCommon[]>(
    []
  );
  const [listDataItem, setListDataItem] = useState<DataItemCommon[]>([]);

  const handleChangeFilter = (filterObj: LmsReportCsCustomerInput) => {
    setFilter(filterObj);
    if (Object.keys(filterObj).length === 0) {
      setListDataItem([]);
      setFirstRender(true);
    } else {
      dispatchAppState({
        payload: {
          value: true
        },
        type: "displayLoader"
      });
      setFirstRender(false);
      fetchData({
        variables: {
          ...valuesPaginationState,
          filter: filterObj
        }
      });
    }
  };

  const message = useMemo(() => {
    if (loading) {
      return {
        icon: InfoIcon,
        message: (
          <Typography variant="body2">
            <span>โปรดกรอกข้อมูลที่จำเป็น (</span>
            <span style={{ color: "#FF2600" }}>{`*`}</span>
            <span>) และกดค้นหา</span>
          </Typography>
        ),
        color: "#555555"
      };
    }
    if (!loading && !listDataItem.length && !firstRender) {
      return {
        icon: XCircle,
        message: intl.formatMessage({
          defaultMessage: "ไม่พบรายการที่เกี่ยวข้อง"
        }),
        color: "#FF2600"
      };
    }
    if (!loading && listDataItem.length && Object.values(filter).length > 0) {
      return {
        icon: CheckCircle,
        message: intl.formatMessage({
          defaultMessage: "พบรายการที่เกี่ยวข้อง"
        }),
        color: "#28A745"
      };
    }
    return {
      icon: InfoIcon,
      message: (
        <MyTypography variant="body3">
          <span>โปรดกรอกข้อมูลที่จำเป็น (</span>
          <span style={{ color: "#FF2600" }}>{`*`}</span>
          <span>) และกดค้นหา</span>
        </MyTypography>
      ),
      color: "#555555"
    };
  }, [listDataItem.length, loading, firstRender, filter]);

  // const handleResetForm = () => {
  //   setFirstRender(true);
  // };

  // START pagination setting

  const { updateListSettings, settings } = useListSettings(
    ListViews.DELIVERY_REPORT_CUSTOMER
  );

  const [valuesPaginationState] = useLocalPaginationState(settings?.rowNumber);

  useEffect(() => {
    updateListSettings(
      "rowNumber",
      defaultListSettings.DELIVERY_REPORT_CUSTOMER.rowNumber
    );
  }, []);

  useEffect(() => {
    if (data?.getLmsReportCsCustomer?.length > 0 && !loading) {
      const listPnNO = data?.getLmsReportCsCustomer || [];
      const newListDataItem = [];
      listPnNO.forEach(dnNoItem => {
        dnNoItem.dataItem.forEach(item => {
          newListDataItem.push({ ...item, ...dnNoItem });
        });
      });

      setBaseListDataItem(newListDataItem);
      handleSort(newListDataItem, { asc: true })(params?.sort);
    } else {
      setBaseListDataItem([]);
      setListDataItem([]);
    }
  }, [data?.getLmsReportCsCustomer]);

  const {
    currentData,
    next,
    prev,
    toFirst,
    toLast,
    disableNext,
    disablePrev
  } = usePaginationLocal(listDataItem, settings.rowNumber);

  const lastPaginationState = () => {
    toLast();
  };

  const handlePreviousFirstPage = () => {
    toFirst();
  };

  const handleNextLastPage = () => {
    lastPaginationState();
  };

  const handlePreviousPage = () => {
    prev();
  };

  const handleNextPage = () => {
    next();
  };

  const handleSort = (itemList?: DataItemCommon[], sort?: { asc: boolean }) => (
    field: DeliveryReportSortField
  ) => {
    const newItemList = itemList || baseListDataItem;
    const sortParam = sort || getSortUrlVariables(field, params);

    const sortItemList = sortingItemList(newItemList, field, sortParam);

    toFirst();
    const list = [...sortItemList];
    setListDataItem(list);

    navigate(
      deliveryReportUrl({
        ...params,
        ...sortParam
      }),
      { replace: true }
    );
  };

  useEffect(() => {
    toFirst();
  }, [settings?.rowNumber]);
  // END pagination setting

  return (
    <>
      <Container className={classes.container}>
        <PageHeader
          className={classes.pageHeader}
          title={intl.formatMessage({
            defaultMessage: "Delivery Report (LMS)",
            description: "label"
          })}
        />
        <DeliveryReportFilterSectionCustomer
          message={<SectionMessage {...message} />}
          // onResetForm={handleResetForm}
          handleChangeFilter={handleChangeFilter}
          listOrder={listDataItem}
        />
        {data?.getLmsReportCsCustomer ? (
          <DeliveryReportTableCustomer
            fetchData={fetchData}
            onPreviousFirstPage={handlePreviousFirstPage}
            onNextLastPage={handleNextLastPage}
            disabled={loading}
            latestPageItemNumber={listDataItem.length}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
            onRowClick={() => null}
            onSort={handleSort()}
            totalCount={listDataItem.length || 0}
            sort={getSortParams(params)}
            onUpdateListSettings={updateListSettings}
            pageInfo={{
              hasNextPage: !disableNext(),
              hasPreviousPage: !disablePrev()
            }}
            settings={settings}
            orderTrackingOutputList={data?.getLmsReportCsCustomer}
            currentData={currentData()}
          />
        ) : (
          ""
        )}
      </Container>
    </>
  );
};

export default DeliveryReportCustomer;
